import { useEffect, useState } from 'react'
import type { AppStatus, CurrentAccount, CurrentLegalDocuments, LegalDocument } from '../types'
import { ApiError, api } from '../lib/api'
import { parsePasswordRecoveryLocation } from '../lib/accountRoute'
import { requiresCommonReconsent } from '../lib/serviceEnrollmentConsent'
import { legalConsentDecisions, legalDocumentsForEnrollment, legalDocumentsUsable } from '../lib/legalConsent'
import { CentralLegalChecklist } from './CentralLegalChecklist'
import { userMessageForError } from '../lib/userError'
import { useI18n } from '../i18n/useI18n'
import { toMemberLegalLocale } from '../lib/legalLocale'

interface AuthPanelProps {
  demoMode?: boolean
  appStatus?: AppStatus | null
  onSuccess?: (account: CurrentAccount) => void
  /** 표준 복구 URL을 닫아 App이 일반 계정 화면으로 돌아가도록 알립니다. */
  onRecoveryRouteClosed?: () => void
  /** 비밀번호 변경 후 재로그인 안내처럼 App에서 전달하는 최초 안내입니다. */
  initialMessage?: string
}

type AuthMode =
  | 'login'
  | 'register'
  | 'enroll'
  | 'findUsername'
  | 'resetRequest'
  | 'resetConfirm'
  | 'resetCancel'

export function AuthPanel({
  demoMode = false,
  appStatus,
  onSuccess,
  onRecoveryRouteClosed,
  initialMessage,
}: AuthPanelProps) {
  const { locale, t } = useI18n()
  const legalLocale = toMemberLegalLocale(locale)
  const developerDemoMode = import.meta.env.DEV && demoMode
  // URL 해석을 한 곳에 모아 App의 화면 선택과 AuthPanel의 폼 선택이 서로
  // 다르게 판단하지 않도록 합니다. 토큰은 React 상태에만 두며 로그나 저장소에
  // 기록하지 않습니다.
  const recoveryLocation = parsePasswordRecoveryLocation(location.pathname, location.search)
  const initialResetToken = recoveryLocation.resetToken
  const initialCancelToken = recoveryLocation.cancelToken
  const initialCancelUserId = recoveryLocation.cancelUserId
  const initialMode: AuthMode = recoveryLocation.mode ?? 'login'

  const [mode, setMode] = useState<AuthMode>(initialMode)
  const [message, setMessage] = useState(
    initialMessage
      ?? (initialMode === 'resetConfirm'
        ? t('auth.message.resetConfirmIntro')
        : recoveryLocation.standardResetPath
          ? t('auth.message.resetTokenMissing')
        : initialMode === 'resetCancel'
          ? t('auth.message.resetCancelIntro')
          : developerDemoMode
            ? t('auth.message.demoLogin')
            : t('auth.message.loginIntro')),
  )
  const [submitting, setSubmitting] = useState(false)
  const [registerUsername, setRegisterUsername] = useState('')
  const [availabilityMessage, setAvailabilityMessage] = useState('')
  const [checkingUsername, setCheckingUsername] = useState(false)
  const [enrollmentUsername, setEnrollmentUsername] = useState('')
  const [reactivation, setReactivation] = useState(false)
  const [commonReconsentRequired, setCommonReconsentRequired] = useState(false)
  const [legalDocuments, setLegalDocuments] = useState<CurrentLegalDocuments | null>(null)
  const [legalLoading, setLegalLoading] = useState(false)
  const [legalError, setLegalError] = useState('')
  const [legalRefreshNonce, setLegalRefreshNonce] = useState(0)
  const [resetToken, setResetToken] = useState(initialResetToken)
  const [cancelToken, setCancelToken] = useState(initialCancelToken)
  const [cancelUserId, setCancelUserId] = useState(initialCancelUserId)

  useEffect(() => {
    if (mode !== 'register' && mode !== 'enroll') return

    let cancelled = false
    setLegalLoading(true)
    setLegalError('')
    void api.currentLegalDocuments(legalLocale)
      .then((documents) => {
        if (cancelled) return
        setLegalDocuments(documents)
      })
      .catch((error) => {
        if (cancelled) return
        setLegalDocuments(null)
        if (error instanceof ApiError && error.code === 'FINAL_LEGAL_DOCUMENTS_NOT_READY') {
          setLegalError(t('auth.message.legalPreparing'))
        } else {
          setLegalError(userMessageForError(error, t('auth.error.legalLoad')))
        }
      })
      .finally(() => {
        if (!cancelled) setLegalLoading(false)
      })

    return () => { cancelled = true }
  }, [mode, legalLocale, legalRefreshNonce])

  function changeMode(nextMode: 'login' | 'register') {
    setMode(nextMode)
    setAvailabilityMessage('')
    setEnrollmentUsername('')
    setReactivation(false)
    setCommonReconsentRequired(false)
    setLegalDocuments(null)
    setLegalError('')
    setMessage(
      nextMode === 'login'
        ? developerDemoMode
          ? t('auth.message.demoLogin')
          : t('auth.message.loginIntro')
        : t('auth.message.registerIntro'),
    )
  }

  function openRecovery(nextMode: 'findUsername' | 'resetRequest') {
    setMode(nextMode)
    setMessage(
      nextMode === 'findUsername'
        ? t('auth.message.findUsernameIntro')
        : t('auth.message.resetRequestIntro'),
    )
  }

  function clearRecoveryQueryAndReturnToLogin(messageText: string) {
    history.replaceState({}, '', '/')
    setResetToken('')
    setCancelToken('')
    setCancelUserId(0)
    setMode('login')
    setMessage(messageText)
    onRecoveryRouteClosed?.()
  }

  function returnFromRecoveryToLogin() {
    clearRecoveryQueryAndReturnToLogin(
      developerDemoMode
        ? t('auth.message.demoLogin')
        : t('auth.message.loginIntro'),
    )
  }

  async function checkUsername() {
    setCheckingUsername(true)
    try {
      const result = await api.usernameAvailability(registerUsername)
      setAvailabilityMessage(result.available ? t('auth.username.available') : t('auth.username.unavailable'))
    } catch (error) {
      setAvailabilityMessage(userMessageForError(error, t('auth.error.usernameCheck')))
    } finally {
      setCheckingUsername(false)
    }
  }

  async function login(username: string, password: string) {
    setSubmitting(true)
    try {
      const account = await api.login(username, password)
      setMessage(t('auth.message.loginSuccess', { name: account.displayName || account.username }))
      onSuccess?.(account)
    } catch (error) {
      if (error instanceof ApiError && (
        error.code === 'SERVICE_ENROLLMENT_REQUIRED'
        || error.code === 'SERVICE_REACTIVATION_REQUIRED'
        || requiresCommonReconsent(error)
      )) {
        const commonRequired = requiresCommonReconsent(error)
        setEnrollmentUsername(username.trim())
        setReactivation(error.code === 'SERVICE_REACTIVATION_REQUIRED')
        setCommonReconsentRequired(commonRequired)
        setMode('enroll')
        setMessage(
          commonRequired
            ? t('auth.message.commonLegalRequired')
            : t('auth.message.serviceLegalRequired'),
        )
      } else {
        setMessage(userMessageForError(error, t('auth.error.login')))
      }
    } finally {
      setSubmitting(false)
    }
  }

  function registrationLegalDocuments(): LegalDocument[] {
    if (!legalDocuments) return []
    return [...legalDocuments.commonDocuments, ...legalDocuments.serviceDocuments]
  }

  function enrollmentLegalDocuments(): LegalDocument[] {
    if (!legalDocuments) return []
    return legalDocumentsForEnrollment(legalDocuments, commonReconsentRequired)
  }

  function legalReadyFor(documents: LegalDocument[]): boolean {
    return !legalLoading && !legalError && legalDocumentsUsable(documents)
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)

    if (mode === 'login') {
      await login(String(form.get('username')), String(form.get('password')))
      return
    }

    if (mode === 'findUsername') {
      setSubmitting(true)
      try {
        await api.requestUsernameReminder(String(form.get('recoveryEmail')))
        setMessage(t('auth.message.usernameReminderSent'))
      } catch (error) {
        setMessage(userMessageForError(error, t('auth.error.usernameReminder')))
      } finally {
        setSubmitting(false)
      }
      return
    }

    if (mode === 'resetRequest') {
      setSubmitting(true)
      try {
        await api.requestPasswordReset(
          String(form.get('username')),
          String(form.get('recoveryEmail')),
        )
        setMessage(t('auth.message.resetMailSent'))
      } catch (error) {
        setMessage(userMessageForError(error, t('auth.error.resetRequest')))
      } finally {
        setSubmitting(false)
      }
      return
    }

    if (mode === 'resetConfirm') {
      setSubmitting(true)
      try {
        await api.confirmPasswordReset(
          resetToken,
          String(form.get('newPassword')),
          String(form.get('newPasswordConfirm')),
        )
        clearRecoveryQueryAndReturnToLogin(t('auth.message.passwordChanged'))
      } catch (error) {
        setMessage(userMessageForError(error, t('auth.error.passwordChange')))
      } finally {
        setSubmitting(false)
      }
      return
    }

    if (mode === 'resetCancel') {
      setSubmitting(true)
      try {
        await api.cancelPasswordReset(cancelUserId, cancelToken)
        clearRecoveryQueryAndReturnToLogin(t('auth.message.resetCancelled'))
      } catch (error) {
        setMessage(userMessageForError(error, t('auth.error.resetCancel')))
      } finally {
        setSubmitting(false)
      }
      return
    }

    if (mode === 'enroll') {
      setSubmitting(true)
      try {
        const documents = enrollmentLegalDocuments()
        if (!legalReadyFor(documents)) {
          setMessage(legalError || t('auth.message.reviewCurrentLegal'))
          return
        }
        const account = await api.serviceEnrollment({
          username: String(form.get('username')),
          password: String(form.get('password')),
          legalLocale: legalDocuments!.requestedLocale,
          legalConsents: legalConsentDecisions(form, documents),
        })
        setMessage(reactivation
          ? t('auth.message.reactivationComplete')
          : t('auth.message.enrollmentComplete'))
        onSuccess?.(account)
      } catch (error) {
        if (error instanceof ApiError && error.code === 'LEGAL_CONTRACT_CHANGED') {
          setLegalDocuments(null)
          setLegalRefreshNonce((value) => value + 1)
          setMode('enroll')
          setMessage(t('auth.message.legalChanged'))
        } else if (requiresCommonReconsent(error)) {
          setCommonReconsentRequired(true)
          setMessage(t('auth.message.legalNeedsReview'))
        } else {
          setMessage(userMessageForError(error, t('auth.error.enrollment')))
        }
      } finally {
        setSubmitting(false)
      }
      return
    }

    setSubmitting(true)
    try {
      const documents = registrationLegalDocuments()
      if (!legalReadyFor(documents)) {
        setMessage(legalError || t('auth.message.reviewCurrentLegal'))
        return
      }
      const account = await api.register({
        username: String(form.get('username')),
        password: String(form.get('password')),
        passwordConfirm: String(form.get('passwordConfirm')),
        displayName: String(form.get('displayName')),
        recoveryEmail: String(form.get('recoveryEmail')),
        legalLocale: legalDocuments!.requestedLocale,
        legalConsents: legalConsentDecisions(form, documents),
      })
      setMessage(t('auth.message.registrationComplete'))
      onSuccess?.(account)
    } catch (error) {
      if (error instanceof ApiError && error.code === 'LEGAL_CONTRACT_CHANGED') {
        setLegalDocuments(null)
        setLegalRefreshNonce((value) => value + 1)
        setMessage(t('auth.message.legalChanged'))
      } else {
        setMessage(userMessageForError(error, t('auth.error.accountRequest')))
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (mode === 'enroll') {
    return (
      <section className="card auth-card">
        <div className="tab-row">
          <button type="button" className="tab active">{t('auth.tab.serviceEnrollment')}</button>
          <button type="button" className="tab" onClick={() => changeMode('login')}>{t('auth.action.backToLogin')}</button>
        </div>

        <div className="service-enrollment-intro">
          <strong>{reactivation ? t('auth.enroll.reactivateTitle') : t('auth.enroll.title')}</strong>
          <p>
            {commonReconsentRequired
              ? t('auth.enroll.reviewCommonAndService')
              : t('auth.enroll.reviewService')}
          </p>
        </div>

        <form onSubmit={(event) => void submit(event)}>
          <label>
            {t('auth.field.username')}
            <input name="username" value={enrollmentUsername} readOnly autoComplete="username" />
          </label>
          <label>
            {t('auth.field.currentPassword')}
            <input name="password" type="password" required autoComplete="current-password" autoFocus />
          </label>
          {legalLoading && <p className="muted">{t('auth.legal.loading')}</p>}
          {legalError && <p className="notice" role="alert">{legalError}</p>}
          {legalDocuments && commonReconsentRequired && (
            <CentralLegalChecklist title={t('auth.legal.commonTitle')} documents={legalDocuments.commonDocuments} />
          )}
          {legalDocuments && (
            <CentralLegalChecklist title={t('auth.legal.serviceTitle')} documents={legalDocuments.serviceDocuments} />
          )}
          <button
            className="button primary large"
            type="submit"
            disabled={submitting || !legalReadyFor(enrollmentLegalDocuments())}
          >
            {submitting ? t('common.processing') : t('auth.action.agreeStart')}
          </button>
        </form>
        <p className="notice" role="status">{message}</p>
        <p className="muted">
          {commonReconsentRequired
            ? t('auth.enroll.selectionContinues')
            : t('auth.enroll.selectionContinues')}
        </p>
      </section>
    )
  }

  if (mode === 'findUsername' || mode === 'resetRequest' || mode === 'resetConfirm' || mode === 'resetCancel') {
    const title = mode === 'findUsername'
      ? t('auth.recovery.findUsernameTitle')
      : mode === 'resetRequest'
        ? t('auth.recovery.resetTitle')
        : mode === 'resetConfirm'
          ? t('auth.recovery.newPasswordTitle')
          : t('auth.recovery.cancelTitle')

    return (
      <section className="card auth-card recovery-card">
        <div className="tab-row">
          <button type="button" className="tab active">{title}</button>
          <button type="button" className="tab" onClick={returnFromRecoveryToLogin}>{t('auth.action.backToLogin')}</button>
        </div>

        <form key={mode} onSubmit={(event) => void submit(event)}>
          {mode === 'findUsername' && (
            <label>
              {t('auth.field.verifiedRecoveryEmail')}
              <input name="recoveryEmail" type="email" required autoComplete="email" autoFocus />
            </label>
          )}

          {mode === 'resetRequest' && (
            <>
              <label>{t('auth.field.username')}<input name="username" required minLength={4} maxLength={40} autoComplete="username" autoFocus /></label>
              <label>{t('auth.field.verifiedRecoveryEmail')}<input name="recoveryEmail" type="email" required autoComplete="email" /></label>
            </>
          )}

          {mode === 'resetConfirm' && (
            <>
              <label>{t('auth.field.newPassword')}<input name="newPassword" type="password" required minLength={10} maxLength={100} autoComplete="new-password" autoFocus /></label>
              <label>{t('auth.field.newPasswordConfirm')}<input name="newPasswordConfirm" type="password" required minLength={10} maxLength={100} autoComplete="new-password" /></label>
              <p className="muted">{t('auth.password.rules')}</p>
            </>
          )}

          {mode === 'resetCancel' && (
            <div className="service-enrollment-intro">
              <strong>{t('auth.recovery.cancelQuestion')}</strong>
              <p>{t('auth.recovery.cancelDescription')}</p>
            </div>
          )}

          <button
            className="button primary large"
            type="submit"
            disabled={submitting}
          >
            {submitting
              ? t('common.processing')
              : mode === 'findUsername'
                ? t('auth.action.requestUsernameMail')
                : mode === 'resetRequest'
                  ? t('auth.action.requestResetMail')
                  : mode === 'resetConfirm'
                    ? t('auth.action.saveNewPassword')
                    : t('auth.action.cancelReset')}
          </button>
        </form>
        <p className="notice" role="status">{message}</p>
        {(mode === 'findUsername' || mode === 'resetRequest') && (
          <p className="muted">{t('auth.recovery.privacyNotice')}</p>
        )}
      </section>
    )
  }

  return (
    <section className="card auth-card">
      <div className="tab-row">
        <button type="button" className={mode === 'login' ? 'tab active' : 'tab'} onClick={() => changeMode('login')}>{t('auth.tab.login')}</button>
        <button type="button" className={mode === 'register' ? 'tab active' : 'tab'} onClick={() => changeMode('register')}>{t('auth.tab.register')}</button>
      </div>

      {developerDemoMode && mode === 'login' && (
        <div className="demo-login-box">
          <div>
            <strong>{t('auth.demo.title')}</strong>
            <small>{t('auth.demo.credentials')}</small>
          </div>
          <button type="button" className="button primary" disabled={submitting} onClick={() => void login('demo-leader', 'demo-password')}>
            {t('auth.demo.login')}
          </button>
        </div>
      )}

      <form key={mode} onSubmit={(event) => void submit(event)}>
        <label>
          {t('auth.field.username')}
          {mode === 'register' ? (
            <input
              name="username"
              required
              autoComplete="username"
              value={registerUsername}
              onChange={(event) => {
                setRegisterUsername(event.target.value)
                setAvailabilityMessage('')
              }}
            />
          ) : (
            <input name="username" required autoComplete="username" defaultValue={developerDemoMode ? 'demo-leader' : ''} />
          )}
        </label>
        {mode === 'register' && (
          <div className="inline-action-row">
            <button type="button" className="button secondary" disabled={checkingUsername || registerUsername.trim().length < 4} onClick={() => void checkUsername()}>
              {checkingUsername ? t('common.checking') : t('auth.action.checkUsername')}
            </button>
            <small role="status">{availabilityMessage}</small>
          </div>
        )}
        <label>
          {t('auth.field.password')}
          <input
            name="password"
            type="password"
            required
            minLength={mode === 'login' ? undefined : 10}
            maxLength={mode === 'login' ? undefined : 100}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            defaultValue={developerDemoMode && mode === 'login' ? 'demo-password' : ''}
          />
        </label>

        {mode === 'login' && !developerDemoMode && (
          <div className="recovery-links" aria-label={t('auth.recovery.ariaLabel')}>
            <button type="button" onClick={() => openRecovery('findUsername')}>{t('auth.recovery.findUsernameTitle')}</button>
            <span aria-hidden="true">·</span>
            <button type="button" onClick={() => openRecovery('resetRequest')}>{t('auth.recovery.resetTitle')}</button>
          </div>
        )}

        {mode === 'register' && (
          <>
            <label>{t('auth.field.passwordConfirm')}<input name="passwordConfirm" type="password" required minLength={10} autoComplete="new-password" /></label>
            <label>{t('auth.field.displayName')}<input name="displayName" required maxLength={100} /></label>
            <label>{t('auth.field.recoveryEmail')}<input name="recoveryEmail" type="email" required /></label>

            {legalLoading && <p className="muted">{t('auth.legal.loading')}</p>}
            {legalError && <p className="notice" role="alert">{legalError}</p>}
            {legalDocuments && (
              <>
                <CentralLegalChecklist title={t('auth.legal.commonTitle')} documents={legalDocuments.commonDocuments} />
                <CentralLegalChecklist title={t('auth.legal.serviceTitle')} documents={legalDocuments.serviceDocuments} />
              </>
            )}
          </>
        )}

        <button
          className="button primary large"
          type="submit"
          disabled={submitting || (mode === 'register' && !legalReadyFor(registrationLegalDocuments()))}
        >
          {submitting ? t('common.processing') : mode === 'login' ? t('auth.action.login') : developerDemoMode ? t('auth.action.createDemoAccount') : t('auth.action.createAccount')}
        </button>
      </form>
      <p className="notice" role="status">{message}</p>
      <p className="muted">
        {developerDemoMode
          ? t('auth.demo.ephemeralNotice')
          : t('auth.legal.continueAfterReview')}
      </p>
    </section>
  )
}
